import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _intersection from 'lodash/intersection';
import _indexOf from 'lodash/indexOf';

// constants
import {
  RED_PLANET,
  AUTOCOMPLETE,
  AUTOCOMPLETE_SELECTED,
  MARKER,
} from '../../lib/Constants';

// actions
import { setPlace, setPlanet } from '../../reducers/Places/actions';

// helpers
import AutocompleteUtils from '../../lib/AutocompleteUtils';

const AutocompletePropTypes = {
  mapLoaded: PropTypes.bool.isRequired, // Lets the container know that the Map has loaded.
  input: PropTypes.string.isRequired, // Value of focused input field
  keyPressed: PropTypes.string.isRequired, // The key pressed by the user
  focused: PropTypes.string.isRequired, // ID of the current/previously focused input field
  dispatchSetPlace: PropTypes.func.isRequired, // Dispatches data for easter egg
  editing: PropTypes.bool.isRequired, // If the user is editing their route
  fieldsLength: PropTypes.number.isRequired, // # of places in the users trip
  dispatchSetPlanet: PropTypes.func.isRequired, // Handles setting origin, stopover, or destination
  easterEgg: PropTypes.bool.isRequired, // Status of the easter egg
};

const AutocompleteDefaultProps = {};

class Autocomplete extends Component {
  constructor(props) {
    super(props);

    this.TIMER = 800;
    this.timeout = null;

    this.dispatchPlanet = this.dispatchPlanet.bind(this);

    this.state = {
      autocompleteMounted: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { autocompleteMounted } = this.state;
    const {
      dispatchSetPlace,
      mapLoaded,
      input,
      keyPressed,
      focused,
      fieldsLength,
      easterEgg,
    } = this.props;

    if (mapLoaded && !autocompleteMounted) this.initAutocompleteService();

    if (input && input !== prevProps.input) this.getPlacePredictions(input);

    if (keyPressed === 'ArrowUp' || keyPressed === 'ArrowDown') {
      AutocompleteUtils.highlightPlace(keyPressed);
    }

    if (keyPressed === 'Enter' && keyPressed !== prevProps.keyPressed) {
      const sanatizedInput = AutocompleteUtils.getAddress();

      if (easterEgg) {
        AutocompleteUtils.getPlace(
          fieldsLength - 1,
          sanatizedInput.toLowerCase() === RED_PLANET.toLowerCase()
            ? this.dispatchPlanet
            : dispatchSetPlace,
        );
      } else {
        AutocompleteUtils.getPlace(fieldsLength - 1, dispatchSetPlace);
      }
    }

    if (keyPressed === 'Escape' && keyPressed !== prevProps.keyPressed) {
      AutocompleteUtils.clearPredictions();
    }

    if (!input || focused !== prevProps.focused) {
      AutocompleteUtils.clearPredictions();
    }
  }

  /**
   * @description Displays Google Maps Autocomplete predictions below the input fields
   *
   * TODO: this may need to be bound to each input field for mobile devices
   *       which provides a great opportunity to refactor this function
   *
   * @param {string} input - User input
   *
   * @returns {void}
   */
  getPlacePredictions(input) {
    const { google } = window;
    // eslint-disable-next-line object-curly-newline
    const { dispatchSetPlace, focused, fieldsLength, easterEgg } = this.props;

    const displaySuggestions = (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) return;

      AutocompleteUtils.clearPredictions();

      // eslint-disable-next-line arrow-parens
      predictions.forEach(prediction => {
        // filter out specific prediction types
        if (
          _intersection(AutocompleteUtils.FORBIDDEN_TYPES, prediction.types)
            .length === 0
        ) {
          const li = document.createElement('li');
          li.innerHTML = '<i class="tsla-icon_map--pin" />';
          li.appendChild(document.createTextNode(prediction.description));
          document.getElementById(AUTOCOMPLETE).appendChild(li);
          // eslint-disable-next-line arrow-parens
          li.addEventListener('click', e => {
            e.currentTarget.className = AUTOCOMPLETE_SELECTED;
            document.querySelectorAll(`[name="${focused}"]`)[0].focus();
            AutocompleteUtils.getPlace(fieldsLength - 1, dispatchSetPlace);
          });
        }
      });

      const sanatizedInput = input.toLowerCase();
      if (_indexOf([sanatizedInput], RED_PLANET.toLowerCase()) >= 0) {
        // comment this line to disable the easter egg
        if (easterEgg) this.appendPredictions();
      }
    };

    if (this.timeout === null) {
      this.timeout = setTimeout(() => {
        this.AutocompleteService.getPlacePredictions(
          { input },
          displaySuggestions,
        );
        this.timeout = null;
      }, this.TIMER);
    }

    if (input.length >= 3) {
      clearTimeout(this.timeout);
      this.timeout = null;
      this.AutocompleteService.getPlacePredictions(
        { input },
        displaySuggestions,
      );
    }
  }

  /**
   * @description Appends easter egg result in autocomplete prediction results
   *
   * @returns {void}
   */
  appendPredictions() {
    const { focused } = this.props;

    const elem = document.createElement('li');
    elem.className = MARKER;
    elem.innerHTML = '<i class="tsla-icon_map--bfr" />';
    elem.appendChild(document.createTextNode(RED_PLANET));
    document.getElementById(AUTOCOMPLETE).appendChild(elem);
    // eslint-disable-next-line arrow-parens
    elem.addEventListener('click', e => {
      e.currentTarget.className = AUTOCOMPLETE_SELECTED;
      document.querySelectorAll(`[name="${focused}"]`)[0].focus();
      try {
        const node = document.getElementsByClassName(AUTOCOMPLETE_SELECTED)[0];
        const address = node.innerText;
        const place = document.activeElement;
        place.value = address;

        this.dispatchPlanet(place);
      } catch (err) {
        AutocompleteUtils.clearPredictions();
      }
    });
  }

  dispatchPlanet(place) {
    const { dispatchSetPlanet } = this.props;
    dispatchSetPlanet({ place, planet: RED_PLANET });
  }

  initAutocompleteService() {
    const { google } = window;

    this.AutocompleteService = new google.maps.places.AutocompleteService();

    this.setState({ autocompleteMounted: true });
  }

  render() {
    const { editing } = this.props;

    return (
      <ul
        id="autocomplete"
        className={classNames('autocomplete--dropdown', { hidden: !editing })}
      />
    );
  }
}

Autocomplete.propTypes = AutocompletePropTypes;

Autocomplete.defaultProps = AutocompleteDefaultProps;

const mapStateToProps = state => ({
  mapLoaded: state.GoogleMap.mapLoaded,
  input: state.Autocomplete.input,
  focused: state.Places.focused,
  editing: state.Places.editing,
  fieldsLength: state.Places.fieldsLength,
  easterEgg: state.App.easterEgg,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetPlace: (field, place, fieldsLen) => {
    dispatch(setPlace(field, place, fieldsLen));
  },
  dispatchSetPlanet: ({ place, planet }) => {
    dispatch(setPlanet({ place, planet }));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Autocomplete);
